import { useCallback, useEffect, useRef } from 'react';

interface UseModalManagementProps {
  isOpen: boolean;
  onClose: (wasCancelled: boolean) => void;
  resetForm?: () => void;
  onCancel?: () => void;
  shouldAllowCancel?: boolean;
}

interface UseModalManagementReturn {
  modalRef: React.RefObject<HTMLBdsModalElement>;
  handleModalClose: (event: CustomEvent) => void;
  handleCancel: () => void;
}

export const useModalManagement = ({
  isOpen,
  onClose,
  resetForm,
  onCancel,
  shouldAllowCancel = true,
}: UseModalManagementProps): UseModalManagementReturn => {
  const modalRef = useRef<HTMLBdsModalElement>(null);

  const handleModalClose = useCallback(
    (event: CustomEvent) => {
      if (event.detail.modalStatus === 'closed') {
        resetForm?.();
        onClose(true);
      }
    },
    [onClose, resetForm],
  );

  const handleCancel = useCallback(() => {
    onCancel?.();
    if (shouldAllowCancel) {
      resetForm?.();
      onClose(true);
    }
  }, [onClose, resetForm, onCancel, shouldAllowCancel]);

  useEffect(() => {
    const modalElement = modalRef && (modalRef.current as HTMLBdsModalElement);
    if (!modalElement) return;

    modalElement.addEventListener('bdsModalChanged', (event) => handleModalClose(event as CustomEvent));

    return () => {
      modalElement.removeEventListener('bdsModalChanged', (event) => handleModalClose(event as CustomEvent));
    };
  }, [handleModalClose]);

  useEffect(() => {
    if (isOpen) {
      resetForm?.();
    }
  }, [isOpen, resetForm]);

  return {
    modalRef,
    handleModalClose,
    handleCancel,
  };
};
